import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Circle, Package, Truck, MapPin, Home } from "lucide-react";

const ICONS = {
  "Order Placed": Package,
  "Shipped from Vendor": Truck,
  "In Transit": Truck,
  "Out for Delivery": MapPin,
  "Delivered": Home,
};

export default function Track() {
  const { awb } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/orders/track/${awb}`).then(({ data }) => setData(data)).catch((e) =>
      setErr(e.response?.data?.detail || "Tracking not found")
    );
  }, [awb]);

  if (err) return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <h1 className="font-display text-3xl text-[#0F3D2E]">{err}</h1>
      <Link to="/" className="mt-4 inline-block text-[#14A085] hover:underline">Back to Home</Link>
    </div>
  );
  if (!data) return <div className="p-10 text-center text-[#3B5249]">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Shipment</div>
      <h1 className="font-display text-3xl md:text-4xl text-[#0F3D2E]">Track your parcel</h1>
      <div className="mt-4 rounded-xl border border-[#E2E8E5] bg-white p-6" data-testid="track-card">
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs text-[#6E877C]">AWB</div>
            <div className="font-mono text-[#0F3D2E]">{data.awb}</div>
          </div>
          <div>
            <div className="text-xs text-[#6E877C]">Carrier</div>
            <div className="text-[#0F3D2E]">{data.carrier}</div>
          </div>
          <div>
            <div className="text-xs text-[#6E877C]">Product</div>
            <div className="text-[#0F3D2E]">{data.product}</div>
          </div>
          <div>
            <div className="text-xs text-[#6E877C]">Sold by</div>
            <div className="text-[#0F3D2E]">{data.vendor_name}</div>
          </div>
          <div className="sm:col-span-2">
            <div className="text-xs text-[#6E877C]">Shipping to</div>
            <div className="text-[#0F3D2E]">{data.destination}</div>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-[#E2E8E5] bg-white p-6" data-testid="track-timeline">
        <h2 className="font-display text-xl text-[#0F3D2E]">Timeline</h2>
        <div className="mt-4 space-y-4">
          {data.timeline.map((t, i) => {
            const Icon = ICONS[t.label] || Package;
            return (
              <div key={i} className="flex items-start gap-3">
                <div className={`h-9 w-9 rounded-full flex items-center justify-center ${t.done ? "bg-[#14A085] text-white" : "bg-[#F9F9F7] text-[#6E877C]"}`}>
                  {t.done ? <CheckCircle2 size={18}/> : <Icon size={18} />}
                </div>
                <div className="flex-1">
                  <div className={`text-sm font-medium ${t.done ? "text-[#0F3D2E]" : "text-[#6E877C]"}`}>{t.label}</div>
                  {t.done && t.date && <div className="text-xs text-[#6E877C]">{new Date(t.date).toLocaleString()}</div>}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-6 text-xs text-[#6E877C]">
          Tracking is currently simulated (MVP). Once we connect a logistics partner (Shiprocket / Delhivery), live status updates will appear here.
        </p>
      </div>
    </div>
  );
}
